import {
  AloftPrice,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateBedsAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];
  if (!subjectImprovements || !compImprovements) return undefined;

  const { bedrooms } = subjectImprovements;
  const { bedrooms: compBedrooms } = compImprovements;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: bedrooms,
      compUnitAmount: compBedrooms,
    });
  }

  // Depreciated cost adjustments didn't account for bedrooms
  return undefined;
};

export const selectBedsAdj = createPriceSelector(
  [
    selectImprovementsEntities,
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'bedrooms'),
  ],
  calculateBedsAdj,
);

export const selectBedsAdjs = createSelector(
  [
    selectImprovementsEntities,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'bedrooms'),
  ],
  (improvements, compSequence, compTypes, salesComparisonApproach, unitRate) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateBedsAdj(
        improvements,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedBedsAdj = createPriceSelector(
  [selectBedsAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'bedrooms', adjSettings),
);

export const selectAppliedBedsAdjDisplay = createSelector([selectAppliedBedsAdj], (adj) => {
  if (!adj) return undefined;

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedBedsAdjsDisplay = createSelector(
  [selectBedsAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, id) => {
      const adj = adjs[id];
      const manualAdjustment = manualAdjustmentsEntities[id]?.bedrooms;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[id] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[id] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'bedrooms', adjSettings),
          ),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
